import { botsSectionFor } from "./settings/sectionMeta";

/**
 * Where one tab of the Bots page links to another.
 *
 * Every one of these used to be `/projects/:id/config#bots`, which left the
 * page to reach a card that has since moved onto it. They are collected here
 * so the `?tab=` spellings live in one file rather than in six string
 * literals, and so a link out of Overview can name a CONFIG KEY and let the
 * section list work out which card holds it.
 *
 * They are used with react-router's <Link>, which PUSHES: a tab the operator
 * arrived at by following a link is a step the Back button has to undo. The
 * tab row itself replaces instead — see BotsPage.
 */

/**
 * settingsLink points at the Settings tab, scrolled to the card that owns
 * `botsPath` when one is named (`bots.simulation.awake_window_minutes` →
 * `#behaviour`).
 */
export function settingsLink(projectID: string, botsPath?: string): string {
  const section = botsPath ? botsSectionFor(botsPath) : null;
  const base = `/projects/${projectID}/bots?tab=settings`;
  return section ? `${base}#${section}` : base;
}

/** sourceLink points at the Source tab — where a bot's name and card come from. */
export function sourceLink(projectID: string): string {
  return `/projects/${projectID}/bots?tab=source`;
}

/**
 * profileSchemaLink points at the Config page's Profile card, which is where
 * `profile.stats_schema` and `profile.meta_schema` are declared.
 *
 * The card-rules editor asks for it four times («declare them in Profile →
 * Stats schema»), and it used to ask with a bare `#profile`. That worked while
 * the editor lived ON the config page; it moved to `?tab=source`, where
 * nothing carries that id, and four links stopped scrolling anywhere while
 * still rewriting the address bar.
 */
export function profileSchemaLink(projectID: string): string {
  return `/projects/${projectID}/config#profile`;
}

/**
 * guildSchemaLink points at the Config page's Guild defaults card, which is
 * where `guild.metadata_schema` is declared.
 *
 * The guild-template dialog needs it for the same reason the card-rules editor
 * needs its sister above: the boxes it draws ARE that schema, so a project
 * that declares nothing leaves the dialog with nothing to draw and the
 * operator with nowhere to go. The anchor is `#guild` and not `#profile` — the
 * same page, a card further down it.
 */
export function guildSchemaLink(projectID: string): string {
  return `/projects/${projectID}/config#guild`;
}

/**
 * helpTypesLink points at the Config page's Help requests card, where a
 * request type's `payload_schema` is declared.
 *
 * The Bots page shows that schema beside the payload templates it governs but
 * does not edit it — the two documents are edited on their own pages, and this
 * is the way across.
 */
export function helpTypesLink(projectID: string): string {
  return `/projects/${projectID}/config#help_request`;
}
